import { captureMessage } from '@sentry/nextjs';
import * as cheerio from 'cheerio';
import { Element } from 'domhandler';

import { APP_BASE_PATH, SEO_CONFIG } from '@/constant/common';
import { getGlobalArgs } from '@/root/global';
import { MetaTagName, PageData } from '@/types/ssr';
import { isInvalidSlug } from '@/utils/routes';

import { curry, joinWithSlash } from './common';
import { skipFileExtension } from './navigation';

export const getMetaTagValue = curry<[MetaTagName, string | undefined], string>((tagName, metaTagsHtmlStr) => {
  if (!metaTagsHtmlStr) return '';
  const $ = cheerio.load(metaTagsHtmlStr);

  return $(`meta[name="${tagName}"]`).attr('content') || '';
});

export const extractMetaTags = (htmlStr?: string): Record<string, string> => {
  if (!htmlStr) return {};

  const existFieldsOnly = (tag: { name: string; content: string }): tag is { name: string; content: string } =>
    Boolean(tag.name && tag.content);

  const getMetaFromElement = (element: Element) => ({
    name: element.attribs.name || element.attribs.property,
    content: element.attribs.content,
  });

  return cheerio
    .load(htmlStr)('meta')
    .toArray()
    .map(getMetaFromElement)
    .filter(existFieldsOnly)
    .reduce((acc, { name, content }) => ({ ...acc, [name]: content }), {});
};

type MakeCommonDescriptionData = {
  title: string;
  platform: string;
  project: string;
};
const makeCommonDescription = ({ title, project, platform }: Partial<MakeCommonDescriptionData>) => {
  if (title && platform) {
    const isCrossPlatform = platform.split(',').length > 1;
    const platformPart = isCrossPlatform ? `для платформ ${platform.replaceAll(',', ', ')}` : `— ${platform}`;
    return `${title} ${platformPart}. Изучите руководство и инструкцию в разделе: ${project || ''}, руководство пользователя в облаке Cloud.ru`;
  }

  return SEO_CONFIG.description;
};

export const makeCanonicalUrl = (slug: string[]) => {
  const getPathName = (slug: string[]) => {
    const joinedSlug = joinWithSlash(slug);
    if (isInvalidSlug(joinedSlug)) return APP_BASE_PATH;
    return `${APP_BASE_PATH}/${joinedSlug}`;
  };

  try {
    return skipFileExtension(
      'html',
      new URL(getPathName(slug), getGlobalArgs('NEXT_PUBLIC_CONTENT_BASE_URL')).toString(),
    );
  } catch (err) {
    captureMessage(`failed to create canonicalUrl: slug: ${slug}, error : ${err}`, { level: 'error' });
    return '';
  }
};

export const getArticleMetaDescription = (pageData: PageData) => {
  const title = pageData.title || SEO_CONFIG.baseTitle;
  const commonDescription = makeCommonDescription({
    platform: getMetaTagValue('platform', pageData?.metatags),
    title: pageData.title,
    project: pageData.project,
  });
  if (title.includes('Документация')) {
    return commonDescription;
  }
  return `Документация: ${commonDescription}`;
};

export const getArticleMetaTitle = (pageData: PageData) => {
  return `${pageData.title || SEO_CONFIG.baseTitle} — документация ${getMetaTagValue('product', pageData.metatags)} руководство пользователя облако Cloud.ru`;
};
